/**
 * Progress photos (F2.7, C-07): PERSONAL — the trainee manages their own
 * (create/update/delete are self-only). A trainer/admin gets a READ-ONLY,
 * visibility-filtered view of a client's photos via the `client/:clientId`
 * routes.
 *
 * Private bucket, and the upload is THREE steps in this order: GET
 * `signed-upload-url` → PUT the file straight to the bucket → POST the row with
 * the filename that came back. The server proves the object exists before it
 * writes, which is impossible in the other order. Reading goes through a
 * short-lived presigned read URL.
 */
import type {
  ApiUpdateProgressPhotoDto,
  ApiCreateProgressPhotoDto,
} from '@mfw/common';

import type {
  CreateProgressPhotoResult,
  ApiProgressPhoto,
  ApiSignedUrl,
  StatsUploadUrl,
} from './types/statsEntries.types';

import { destroy, patch, post, get } from './axios';

/** Step one: a presigned PUT and the filename the row will have to carry. */
export const fetchProgressPhotoUploadUrl = async (
  mediaType: ApiCreateProgressPhotoDto['mediaType'],
) => {
  const response = await get<{ data: StatsUploadUrl }>(
    `/v2/stats/progress-photos/signed-upload-url?mediaType=${encodeURIComponent(mediaType)}`,
  );
  // `response.data` — the presign helper answers `{ data: { url, mediaSource } }`
  // and the `get` wrapper hands back the whole body, exactly as every content
  // presign consumer here already unwraps it (`exercises.api.ts`). Returning
  // `response` made the caller PUT to `undefined`.
  return response?.data ?? null;
};

export const createProgressPhoto = async (dto: ApiCreateProgressPhotoDto) => {
  const response = await post<CreateProgressPhotoResult>(
    '/v2/stats/progress-photos',
    dto,
  );
  // BE returns a raw body, but the `post` helper types the `{data}` wrapper
  return (response ?? null) as CreateProgressPhotoResult | null;
};

// all of the user's photos (newest first) — no filter
export const fetchProgressPhotos = async () => {
  const response = await get<ApiProgressPhoto[]>('/v2/stats/progress-photos');
  return response ?? null;
};

// URL is short-lived — short staleTime/gcTime on the query side
export const fetchProgressPhotoUrl = async ({ id }: { id: string }) => {
  const response = await get<ApiSignedUrl>(
    `/v2/stats/progress-photos/${id}/url`,
  );
  return response ?? null;
};

// trainer/admin READ view of a client's shared photos (BE filters by
// per-photo visibility; 403 = no access/consent, an EXPECTED state)
export const fetchClientProgressPhotos = async (clientId: string) => {
  const response = await get<ApiProgressPhoto[]>(
    `/v2/stats/progress-photos/client/${clientId}`,
  );
  return response ?? null;
};

export const fetchClientProgressPhotoUrl = async ({
  clientId,
  id,
}: {
  clientId: string;
  id: string;
}) => {
  const response = await get<ApiSignedUrl>(
    `/v2/stats/progress-photos/client/${clientId}/${id}/url`,
  );
  return response ?? null;
};

export const updateProgressPhoto = async ({
  id,
  ...dto
}: ApiUpdateProgressPhotoDto & { id: string }) => {
  const response = await patch<ApiProgressPhoto>(
    `/v2/stats/progress-photos/${id}`,
    dto,
  );
  return (response ?? null) as ApiProgressPhoto | null;
};

// also deletes the file from the bucket (best-effort on BE)
export const deleteProgressPhoto = async ({ id }: { id: string }) => {
  await destroy(`/v2/stats/progress-photos/${id}`);
};
